/**
 * Saúde do número (spec 19) — a lista, e a página de um número.
 *
 * ## A tela responde uma pergunta: *"posso aumentar o volume neste número?"*
 *
 * Ela existe porque **nenhum canal diz quem denunciou**. No aparelho vinculado não há nota nenhuma;
 * no oficial há uma nota da Meta, que chega pronta e sem os números que a formaram. O que dá para
 * fazer é medir as SOMBRAS do que a Meta declara olhar — bloqueio, taxa de leitura, denúncia — e
 * mostrá-las com a procedência dita.
 *
 * ## Por que DUAS telas, e não uma
 *
 * A primeira versão empilhava um cartão grande por número: seis indicadores, sinais, nota da Meta e
 * exposição, tudo aberto, repetido para cada conexão. Com três números a página já era uma pilha, e
 * a pergunta que ela existe para responder — *"qual deles precisa de mim?"* — exigia rolar tudo.
 *
 * Agora a **lista** é uma linha por número, feita para varrer: índice, faixa, curva e o que está
 * errado. A profundidade mora na **página do número**, onde a pergunta já é outra (*"o que houve com
 * este?"*) e o detalhe é o conteúdo, não ruído.
 *
 * ## As famílias da v2 (spec 22, 31/10)
 *
 * A lista é uma **Consulta** (`PaginaInteira` + `ListView`: a tabela rola por dentro, a página não). A
 * página do número é o **Cockpit do número**: cabeçalho, a faixa com o índice ao lado da nota da Meta, e
 * uma `GradeDeCartoes` — curva, Meta e exposição, indicadores, sinais. No celular, um cartão por vez.
 *
 * ## As três coisas que estas telas nunca fazem
 *
 * 1. **Não mostram zero onde não mediram.** Sem amostra é "—", com o motivo.
 * 2. **Não somam a nota da Meta ao índice.** GREEN é medida deles; o índice é inferência nossa.
 * 3. **Não escondem punição atrás de média boa.** Ban ativo puxa o teto e aparece antes de tudo.
 */

import { useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { falhaDe, identificadorLegivel } from "@hugi/ui";
import {
  CabecalhoDaTela,
  Carregando,
  Dica,
  Erro,
  GradeDeCartoes,
  ListView,
  PaginaInteira,
  PainelDeIndicadores,
  SemPermissao,
  Selo,
  type CartaoDaGrade,
  type ColunaDaLista,
  type Tom as TomDoKit,
} from "@hugi/ui/kit";
import { Activity } from "lucide-react";
/*
 * A UNIDADE da amostra e o mínimo vêm do módulo puro, e não de uma cópia aqui: os dois lados
 * precisam responder a mesma coisa. Foi por a tela ter opinião própria — `obs.` para todo indicador —
 * que `disponibilidade` apareceu em produção como "604.800 obs.", que são sete dias em segundos.
 */
import { AMOSTRA_EH_CONTAGEM, AMOSTRA_MINIMA } from "../../../src/canal/saude-do-numero.js";
import {
  useSaudeDoNumero,
  useSaudeDosNumeros,
  type IndicadorDeSaude,
  type NumeroComSaude,
  type PontoDaCurva,
} from "./dados.js";
import { nomeDaPunicao } from "./punicoes.js";
import { PausarDoNumero } from "./acoes-do-dono.js";
import "./saude-do-numero.css";

/** As janelas oferecidas. 7 é a padrão porque é a que a META usa para o `quality_rating`. */
const JANELAS = [7, 14, 30] as const;

const ROTULO: Record<IndicadorDeSaude["nome"], { titulo: string; ajuda: string }> = {
  entrega: {
    titulo: "Entrega",
    ajuda: "Das mensagens que saíram, quantas chegaram. É a sombra do bloqueio: quem bloqueou nunca recebe.",
  },
  resposta: {
    titulo: "Resposta",
    ajuda: "Das pessoas que você abordou, quantas responderam alguma coisa.",
  },
  leitura: {
    titulo: "Leitura",
    ajuda: "Das mensagens que chegaram, quantas foram lidas. A Meta usa esta taxa pelo nome.",
  },
  reciprocidade: {
    titulo: "Reciprocidade",
    ajuda: "Recebidas para cada enviada. Muito envio com pouca conversa é o traço de conta de disparo.",
  },
  anomalias: {
    titulo: "Anomalias",
    ajuda: "Falhas do servidor e mensagens que o destinatário não decifrou, por mil enviadas. Menos é melhor.",
  },
  saidasDeGrupo: {
    titulo: "Saídas de grupo",
    ajuda:
      "Quantas pessoas saíram dos grupos nas 24 h seguintes a uma publicação, sobre a audiência alcançada. " +
      "É o sinal mais direto de que acharam a mensagem inconveniente — ninguém sai de um grupo por acaso. " +
      "Menos é melhor, e só existe para quem publica em grupo.",
  },
  rejeicaoEmGrupo: {
    titulo: "Rejeição em grupo",
    ajuda:
      "Vezes em que um admin tirou este número de um grupo ou apagou uma mensagem dele, sobre as publicações em grupo do período. " +
      "É a rejeição dita por quem cuida do grupo. N/A para quem não publica em grupo.",
  },
  disponibilidade: {
    titulo: "Disponibilidade",
    ajuda: "Quanto do período o número esteve de pé. Mede a nossa infraestrutura, não a reputação dele.",
  },
};


const SINAL: Record<string, string> = {
  recibo_server_error: "Falha do servidor ao entregar",
  recibo_retry: "Destinatário pediu reenvio",
  mensagem_indecifravel: "Mensagem que não deciframos",
  envio_recusado: "Envio recusado pelo WhatsApp",
  removido_de_grupo: "Removido de grupo por um admin",
  apagada_por_admin: "Mensagem apagada por um admin",
  conexao_instavel: "Conexão instável",
};

/**
 * O (i) das duas telas. Era um `Porque` solto embaixo da tabela e um `title` no cabeçalho da coluna
 * "Curva" — o `title` não aparece no celular, e a coluna do kit não tem onde pô-lo.
 */
const DICA_DA_LISTA =
  "Como cada número vem se comportando — antes de o WhatsApp agir. " +
  "A janela de 7 dias é a mesma que a Meta usa para calcular a nota dela — é o que torna os dois lados comparáveis. " +
  "Nenhum canal informa quem denunciou um número, nem o oficial nem o por aparelho vinculado: o que esta tela mostra são as sombras disso no que já medimos. " +
  "A curva é o índice dia a dia; ela começa depois do segundo dia medido — com um ponto só não há movimento a mostrar.";

type Tom = "ok" | "degradado" | "ruim" | "neutro";

/** O tom do produto (o mesmo do resto do painel) no tom do kit. */
const NO_KIT: Record<Tom, TomDoKit> = { ok: "sucesso", degradado: "atencao", ruim: "perigo", neutro: "neutro" };

function SeloDoTom(props: { readonly tom: Tom; readonly children: ReactNode }) {
  return <Selo tom={NO_KIT[props.tom]}>{props.children}</Selo>;
}

function tomDaFaixa(f: NumeroComSaude["saude"]["faixa"]): Tom {
  if (f === "bom") return "ok";
  if (f === "atencao") return "degradado";
  if (f === "risco") return "ruim";
  return "neutro";
}

function nomeDaFaixa(f: NumeroComSaude["saude"]["faixa"]): string {
  if (f === "bom") return "Bom";
  if (f === "atencao") return "Atenção";
  if (f === "risco") return "Risco";
  return "Sem amostra";
}

function corDoIndice(indice: number): string {
  return indice >= 80 ? "var(--ok-texto)" : indice >= 60 ? "var(--atencao-texto)" : "var(--erro-texto)";
}

/**
 * A curva do índice, em SVG inline.
 *
 * ## Três decisões que a fazem honesta
 *
 * 1. **Dia sem amostra é BURACO, não zero.** A linha se interrompe: ligar os dois lados desenharia
 *    um mergulho que nunca aconteceu — e um gráfico mentindo é pior que gráfico nenhum, porque ele
 *    convence.
 * 2. **Dia medido isolado vira PONTO**, e não some. A primeira versão descartava corridas de um dia,
 *    e o buraco engolia justamente a única medida que havia ali.
 * 3. **Sem dois pontos, ela não aparece.** Um ponto só vira uma reta que sugere estabilidade onde
 *    não há série.
 */
export function Curva(props: { readonly pontos: readonly PontoDaCurva[]; readonly altura?: number }) {
  const { pontos } = props;
  const comIndice = pontos.filter((p) => p.indice !== null);
  if (comIndice.length < 2) return null;

  const L = 240;
  const A = props.altura ?? 40;
  const x = (i: number) => (pontos.length <= 1 ? 0 : (i / (pontos.length - 1)) * L);
  const y = (v: number) => A - (v / 100) * A;

  const segmentos: string[][] = [];
  const soltos: string[] = [];
  let atual: string[] = [];
  const fechar = () => {
    if (atual.length > 1) segmentos.push(atual);
    else if (atual.length === 1) soltos.push(atual[0]!);
    atual = [];
  };
  pontos.forEach((p, i) => {
    if (p.indice === null) {
      fechar();
      return;
    }
    atual.push(`${x(i).toFixed(1)},${y(p.indice).toFixed(1)}`);
  });
  fechar();

  // Os MESMOS três tokens semânticos dos selos (`vidro.css`), e não uma paleta nova: a linha e o
  // selo ficam lado a lado, e duas escalas de cor para a mesma faixa é como uma delas envelhece.
  const cor = corDoIndice(comIndice[comIndice.length - 1]!.indice!);

  return (
    <svg viewBox={`0 0 ${L} ${A}`} width="100%" height={A} preserveAspectRatio="none" role="img"
         aria-label={`Curva do índice nos últimos ${pontos.length} dias`}>
      {/* As duas réguas: 80 e 60, os cortes das faixas. Sem elas o traço não diz o que é "bom". */}
      <line x1="0" y1={y(80)} x2={L} y2={y(80)} stroke="var(--borda)" strokeWidth="1" strokeDasharray="2 3" />
      <line x1="0" y1={y(60)} x2={L} y2={y(60)} stroke="var(--borda)" strokeWidth="1" strokeDasharray="2 3" />
      {segmentos.map((pts, i) => (
        <polyline key={i} points={pts.join(" ")} fill="none" stroke={cor} strokeWidth="1.5"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      ))}
      {soltos.map((pt, i) => {
        const [cx, cy] = pt.split(",");
        return <circle key={i} cx={cx} cy={cy} r="1.8" fill={cor} />;
      })}
    </svg>
  );
}

/** A variação do período. `null` NÃO vira 0: zero se lê como "estável", que é uma afirmação. */
function Variacao(props: { readonly v: number | null | undefined }) {
  const v = props.v;
  if (v === null || v === undefined) return <span className="hugi-saude-fraco">—</span>;
  return (
    <span className={v < 0 ? "hugi-saude-queda" : "hugi-saude-forte"}>
      {v > 0 ? "+" : ""}
      {v}
    </span>
  );
}

/**
 * A coluna "Situação": o que está errado, em ordem de gravidade.
 *
 * Punição vence nota da Meta, que vence salto de exposição, que vence silêncio. Mostrar as quatro de
 * uma vez encheria a linha com o mesmo fato dito de vários jeitos — e a lista existe para varrer.
 */
function Situacao(props: { readonly n: NumeroComSaude }) {
  const { n } = props;
  const p = n.saude.punicoesAtivas[0];
  if (p) return <SeloDoTom tom="ruim">{nomeDaPunicao(p)}</SeloDoTom>;
  if (n.qualidadeDaMeta && n.qualidadeDaMeta !== "GREEN") {
    return <SeloDoTom tom={n.qualidadeDaMeta === "YELLOW" ? "degradado" : "ruim"}>{`Meta: ${n.qualidadeDaMeta}`}</SeloDoTom>;
  }
  const e = n.exposicao;
  if (e && e.razao !== null && e.razao >= 2) {
    return <SeloDoTom tom="degradado">{`${e.razao.toFixed(1)}× pessoas novas`}</SeloDoTom>;
  }
  return <span className="hugi-saude-fraco">—</span>;
}

/**
 * Falha da consulta → o estado do kit. Sem permissão diz QUAL falta (tentar de novo não resolve);
 * o resto é erro com a frase do servidor, quando ele manda uma, e o tentar de novo.
 */
function EstadoDaFalha(props: { readonly erro: unknown; readonly aoRepetir: () => void }) {
  const falha = falhaDe(props.erro);
  if (falha?.tipo === "sem_permissao") {
    return <SemPermissao>{falha.permissao ? `Ver isto exige a permissão ${falha.permissao}.` : undefined}</SemPermissao>;
  }
  return <Erro aoTentarDeNovo={props.aoRepetir}>{falha?.erro.mensagem ?? "Não deu para carregar a saúde do número."}</Erro>;
}

function SeletorDePeriodo(props: { readonly dias: number; readonly aoMudar: (dias: number) => void }) {
  return (
    <label className="hugi-saude-periodo">
      <span className="hugi-saude-periodo-rotulo">Período</span>
      <select value={props.dias} onChange={(e) => props.aoMudar(Number(e.target.value))}>
        {JANELAS.map((d) => (
          <option key={d} value={d}>{d} dias</option>
        ))}
      </select>
    </label>
  );
}

const COLUNAS: ColunaDaLista<NumeroComSaude>[] = [
  {
    id: "numero",
    rotulo: "Número",
    principal: true,
    link: true,
    celula: (n) => identificadorLegivel(n.rotulo),
    ordenar: (n) => n.rotulo,
  },
  { id: "canal", rotulo: "Canal", celula: (n) => (n.modo === "nao_oficial" ? "API Hugi" : "API oficial") },
  {
    id: "indice",
    rotulo: "Índice",
    numerica: true,
    ordenar: (n) => n.saude.indice,
    celula: (n) => (
      <span className="hugi-saude-indice" style={n.saude.indice === null ? undefined : { color: corDoIndice(n.saude.indice) }}>
        {n.saude.indice === null ? "—" : n.saude.indice}
      </span>
    ),
  },
  { id: "faixa", rotulo: "Faixa", celula: (n) => <SeloDoTom tom={tomDaFaixa(n.saude.faixa)}>{nomeDaFaixa(n.saude.faixa)}</SeloDoTom> },
  {
    id: "curva",
    rotulo: "Curva",
    classe: () => "hugi-saude-celula-curva",
    /*
      Sem dois pontos, um TRAÇO — como "Variação" e "Situação" já fazem.
      A frase "a partir do 2º dia" ficava repetida em TODAS as linhas (seis vezes, na captura de
      produção): é explicação da coluna, não dado da linha, e por isso mora no (i) da tela.
    */
    celula: (n) => ((n.curva ?? []).length >= 2 ? <Curva pontos={n.curva ?? []} altura={24} /> : <span className="hugi-saude-fraco">—</span>),
  },
  { id: "variacao", rotulo: "Variação", numerica: true, ordenar: (n) => n.variacao ?? null, celula: (n) => <Variacao v={n.variacao} /> },
  { id: "situacao", rotulo: "Situação", celula: (n) => <Situacao n={n} /> },
];

export function SaudeDoNumero() {
  const [dias, setDias] = useState<number>(7);
  const [, navegar] = useLocation();
  const consulta = useSaudeDosNumeros(dias);

  return (
    <PaginaInteira rotulo="Saúde do número" classe="hugi-saude">
      <ListView
        titulo="Saúde do número"
        icone={<Activity size={18} />}
        colunas={COLUNAS}
        linhas={consulta.data?.itens ? [...consulta.data.itens] : undefined}
        chave={(n) => n.conexaoId}
        estado={{
          carregando: consulta.isPending,
          erro: consulta.isError,
          ...(consulta.isError ? { mensagemDeErro: falhaDe(consulta.error)?.erro.mensagem ?? "Não deu para carregar a saúde dos números." } : {}),
          semPermissao: falhaDe(consulta.error)?.tipo === "sem_permissao",
          aoTentarDeNovo: () => void consulta.refetch(),
        }}
        abrir={{ href: (n) => `/canais/saude/${n.conexaoId}`, aoAbrir: (n) => navegar(`/canais/saude/${n.conexaoId}`) }}
        contagem={(n) => (n === 1 ? "1 número" : `${n} números`)}
        vazio={{
          semNada: (
            <>
              <strong>Nenhum número de WhatsApp conectado</strong>{" "}
              <Link href="/canais/conectar" className="v2-link">Conectar um número</Link>
            </>
          ),
        }}
        acoes={
          <>
            <SeletorDePeriodo dias={dias} aoMudar={setDias} />
            <Dica texto={DICA_DA_LISTA} />
          </>
        }
      />
    </PaginaInteira>
  );
}

// --- A página de UM número -------------------------------------------------------------------------

/**
 * Quanto tempo, dito curto — a amostra de `disponibilidade` são os SEGUNDOS da janela.
 *
 * Em produção a tela imprimia **"604.800 obs."** nessa linha: sete dias em segundos, com o rótulo de
 * observações. Não era só feio — dizia um fato falso sobre o que foi medido.
 */
function duracaoCurta(segundos: number): string {
  if (segundos >= 86_400) {
    const d = Math.round(segundos / 86_400);
    return `${d} ${d === 1 ? "dia" : "dias"}`;
  }
  if (segundos >= 3_600) return `${Math.round(segundos / 3_600)} h`;
  if (segundos >= 60) return `${Math.round(segundos / 60)} min`;
  return `${segundos} s`;
}

/**
 * A amostra na unidade DELA — e, quando ela ainda não basta, a DISTÂNCIA até bastar.
 *
 * `8 obs.` diz o que houve. `8 de 20` diz o que houve **e** quanto falta, no mesmo espaço. A segunda
 * forma é a que responde a pergunta de quem abriu a tela e não achou nota nenhuma.
 */
function amostraLegivel(i: IndicadorDeSaude): string {
  if (!AMOSTRA_EH_CONTAGEM.has(i.nome)) return `${duracaoCurta(i.amostra)} observados`;
  if (i.motivo === "amostra_pequena") return `${i.amostra.toLocaleString("pt-BR")} de ${AMOSTRA_MINIMA}`;
  return `${i.amostra.toLocaleString("pt-BR")} obs.`;
}

/**
 * Uma linha da tabela de indicadores — e ela emite **as quatro células, sempre**.
 *
 * ⚠️ Antes, os dois casos sem veredito usavam `colSpan` para engolir as colunas da direita. O efeito
 * em produção foi uma **coluna "Peso" vazia em cinco das sete linhas**: o cabeçalho prometia um dado
 * que a tabela não entregava, e quem olhava concluía que a coluna estava quebrada.
 *
 * O peso agora aparece SEMPRE, e é ele que carrega o veredito: **riscado quando não contou**. Isso
 * troca cinco repetições da frase *"pouco para concluir"* por uma marca visual que se lê de relance
 * — e responde de graça a pergunta que a tabela existe para responder: *quais pesos formaram o
 * índice?* Na captura de produção, a resposta era "só o 10 da disponibilidade", e não dava para ver.
 */
function LinhaDeIndicador(props: { readonly i: IndicadorDeSaude }) {
  const { i } = props;
  const r = ROTULO[i.nome];

  /*
   * AMOSTRA PEQUENA mostra o VALOR, e diz que ele não conta.
   *
   * Escondê-lo seria pior que mostrá-lo: em produção, no primeiro dia de dados reais, um número
   * com 5 envios apareceu em "Risco" por causa de uma entrega de 0% sobre 5 — e o mesmo número
   * tinha 67% de resposta, ou seja, quem respondeu recebeu. O valor é informação; o que ele não
   * pode é virar veredito sozinho.
   */
  const pequena = !i.aplicavel && i.motivo === "amostra_pequena" && i.valor !== null;
  const semValor = !pequena && (!i.aplicavel || i.valor === null || i.nota === null);
  const pct = i.valor === null ? null : Math.round(i.valor * 100);

  const porQueNaoContou = pequena
    ? `Não entrou no índice: ${i.amostra} observações, e o mínimo é ${AMOSTRA_MINIMA}.`
    : "Não entrou no índice: sem amostra no período.";

  return (
    <tr title={r.ajuda}>
      <td className={semValor ? "hugi-saude-fraco" : undefined}>{r.titulo}</td>

      <td className="is-num">
        {semValor ? (
          <span className="hugi-saude-fraco">—</span>
        ) : pequena ? (
          <span className="hugi-saude-fraco">{pct}%</span>
        ) : (
          <SeloDoTom tom={i.nota! >= 80 ? "ok" : i.nota! >= 60 ? "degradado" : "ruim"}>{`${pct}%`}</SeloDoTom>
        )}
      </td>

      <td className="is-num hugi-saude-fraco hugi-saude-miudo">
        {semValor && i.amostra === 0 ? "sem amostra" : amostraLegivel(i)}
      </td>

      <td className="is-num hugi-saude-miudo">
        {i.aplicavel ? (
          <span className="hugi-saude-fraco">{i.peso}</span>
        ) : (
          <span className="hugi-saude-fraco hugi-saude-riscado" title={porQueNaoContou}>
            {i.peso}
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * A nota da Meta.
 *
 * ⚠️ Aparelho vinculado mostra **"não existe neste canal"**, e não "desconhecida". As duas levam a
 * perguntas diferentes: uma some com uma leitura, a outra não some nunca — e quem lê "desconhecida"
 * abre chamado pedindo para alguém ir buscar.
 */
function NotaDaMeta(props: { readonly n: NumeroComSaude }) {
  const { n } = props;
  if (n.modo === "nao_oficial") {
    return (
      <div className="hugi-saude-linha hugi-saude-fraco">
        Sem nota da Meta neste canal
        <Dica texto="O WhatsApp não dá nota de qualidade a número por aparelho vinculado. O índice ao lado é nosso, calculado com o que este canal deixa medir." />
      </div>
    );
  }
  if (!n.qualidadeDaMeta) {
    return (
      <div className="hugi-saude-linha hugi-saude-fraco">
        A Meta ainda não devolveu nota
        <Dica texto="Ela aparece depois do primeiro envio por este número." />
      </div>
    );
  }
  const tom: Tom = n.qualidadeDaMeta === "GREEN" ? "ok" : n.qualidadeDaMeta === "YELLOW" ? "degradado" : "ruim";
  return (
    <div className="hugi-saude-linha">
      <SeloDoTom tom={tom}>{`Meta: ${n.qualidadeDaMeta}`}</SeloDoTom>
      {n.tier ? <SeloDoTom tom="neutro">{n.tier}</SeloDoTom> : null}
      {n.limiteDiario !== null ? (
        <span className="hugi-saude-fraco hugi-saude-miudo">
          {n.limiteDiario.toLocaleString("pt-BR")} destinatários únicos por 24 h
        </span>
      ) : null}
      {n.qualidadeLidaEm ? (
        <span className="hugi-saude-fraco hugi-saude-miudo">
          lida em {new Date(n.qualidadeLidaEm).toLocaleString("pt-BR")}
        </span>
      ) : null}
    </div>
  );
}

/**
 * O que falta para esta tela poder pontuar — em número, não em desculpa.
 *
 * Mostra o indicador de reputação que está MAIS PERTO do mínimo, porque basta um passar para o
 * índice nascer. `disponibilidade` fica de fora de propósito: a amostra dela é tempo, ela sempre
 * tem, e foi exatamente por ela carregar a média sozinha que um número que nunca enviou nada
 * apareceu como "Bom, 100" na primeira versão desta tela.
 */
function FaltaMedir(props: { readonly s: NumeroComSaude["saude"] }) {
  const { s } = props;
  const deTaxa = s.indicadores.filter((i) => AMOSTRA_EH_CONTAGEM.has(i.nome));
  const maisPerto = deTaxa.reduce<(typeof deTaxa)[number] | null>(
    (melhor, i) => (melhor === null || i.amostra > melhor.amostra ? i : melhor),
    null,
  );
  const nada = !maisPerto || maisPerto.amostra === 0;
  const faltam = maisPerto ? Math.max(0, AMOSTRA_MINIMA - maisPerto.amostra) : AMOSTRA_MINIMA;

  if (nada) return <p className="hugi-saude-fraco hugi-saude-miolo">Nada saiu por ele no período. A conta começa no primeiro envio.</p>;
  return (
    <div className="hugi-saude-falta hugi-saude-miolo">
      <p>
        {/* ⚠️ O número e a palavra ficam no MESMO nó — partir num `<strong>` só do número
            quebraria a busca por "12 observações", que é como o teste (e uma pessoa) procura. */}
        Falta pouco:{" "}
        <strong className="hugi-saude-forte">
          {`${faltam} ${faltam === 1 ? "observação" : "observações"} em ${ROTULO[maisPerto.nome].titulo.toLowerCase()}`}
        </strong>
        {` — é o indicador mais perto do mínimo de ${AMOSTRA_MINIMA}.`}
      </p>
      {/*
        A barra é o mesmo fato, para quem varre em vez de ler — e fica SEM legenda de propósito:
        a linha da tabela já diz "8 de 20". Três repetições do mesmo número era o defeito que
        este bloco veio consertar, não um a imitar.
      */}
      <div className="hugi-saude-barra">
        <i style={{ width: `${Math.min(100, Math.round((maisPerto.amostra / AMOSTRA_MINIMA) * 100))}%` }} />
      </div>
    </div>
  );
}

function Exposicao(props: { readonly n: NumeroComSaude }) {
  const e = props.n.exposicao;
  if (!e || e.novosNaJanela <= 0) return null;
  return (
    <div className="hugi-saude-linha">
      <span className="hugi-saude-forte">Pessoas novas abordadas</span>
      {/*
        ⚠️ As três medidas ficam em UM nó de texto de propósito. Partir o número num `<strong>`
        deixaria a frase mais legível e quebraria três testes que a procuram inteira — e o
        contrato deles (o dado ESTÁ na tela) vale mais que o negrito.
      */}
      <span className="hugi-saude-fraco hugi-saude-tabular">
        {`${e.novosNaJanela.toLocaleString("pt-BR")} no período`}
        {e.ultimoDia ? ` · ${e.ultimoDia.novos.toLocaleString("pt-BR")} no último dia` : ""}
        {e.mediaAnterior !== null
          ? ` · média antes: ${e.mediaAnterior.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
          : ""}
      </span>
      {e.razao !== null && e.razao >= 2 ? <SeloDoTom tom="degradado">{`${e.razao.toFixed(1)}× a média`}</SeloDoTom> : null}
      <Dica texto="Abordar muita gente que nunca falou com você é o que dispara o bloqueio mais comum do WhatsApp. Isto não entra no índice de propósito: campanha legítima tem exposição alta por desenho — o que informa é o salto sobre o seu próprio histórico." />
    </div>
  );
}

export function NumeroDetalhe(props: { readonly id: string }) {
  const [dias, setDias] = useState<number>(7);
  const consulta = useSaudeDoNumero(props.id, dias);
  const n = consulta.data;

  const cabecalho = (titulo: string, recorte?: ReactNode, primaria?: ReactNode) => (
    <CabecalhoDaTela
      icone={<Activity size={18} />}
      titulo={titulo}
      {...(recorte ? { recorte } : {})}
      ferramentas={<SeletorDePeriodo dias={dias} aoMudar={setDias} />}
      secundarias={<Link href="/canais/saude" className="v2-botao">← Todos os números</Link>}
      {...(primaria ? { primaria } : {})}
    />
  );

  if (consulta.isPending) {
    return <PaginaInteira rotulo="Saúde do número" classe="hugi-saude">{cabecalho("Saúde do número")}<Carregando /></PaginaInteira>;
  }
  if (consulta.isError || !n) {
    return (
      <PaginaInteira rotulo="Saúde do número" classe="hugi-saude">
        {cabecalho("Saúde do número")}
        <EstadoDaFalha erro={consulta.error} aoRepetir={() => void consulta.refetch()} />
      </PaginaInteira>
    );
  }

  const s = n.saude;
  const curva = n.curva ?? [];
  const sinais = Object.entries(n.sinais ?? {}).filter(([, q]) => q > 0);
  const comIndice = s.indice !== null;
  /*
   * "SEGURE O VOLUME" COM O BOTÃO AO LADO (spec 23, jornada 4). A frase dizia para não aumentar o volume e deixava a
   * pessoa ir caçar, tela por tela, as campanhas e publicações deste número. Agora o aviso traz o verbo: pausar o que
   * sai por ele, com confirmação, só para quem tem a permissão da rota e só quando há algo saindo. Ele é a ação
   * primária do cabeçalho: na faixa de punições (que rola por dentro) ou embaixo da frase, sumia da vista.
   */
  const comPunicao = s.punicoesAtivas.length > 0;
  const pedeSegurar = comPunicao || s.faixa === "risco" || s.faixa === "atencao";

  const cartoes: CartaoDaGrade[] = [
    /*
      SEM ÍNDICE, a página não repete que não sabe — ela diz QUANTO FALTA para saber.
      --------------------------------------------------------------------------------------------
      Em produção, um número com 8 observações abria assim: dois cartões grandes e vazios ("—" e
      "A curva começa depois do segundo dia medido"), a nota da Meta ausente, e seis das sete
      linhas riscadas. Quatro maneiras de dizer *não sei*, ocupando metade da tela — e o owner
      resumiu em uma palavra: "nada". A tela SABIA a coisa que faltava dizer: entrega tem 8 de 20
      observações. Por isso, sem índice, não há faixa nem curva: há este cartão.
    */
    comIndice
      ? {
          id: "curva",
          area: "curva",
          titulo: curva.length >= 2 ? `Últimos ${curva.length} dia(s)` : "Curva",
          rotuloCurto: "Curva",
          conteudo: (
            <div className="hugi-saude-curva hugi-saude-miolo">
              {/* Sem índice, a frase SAI — o cartão de cima diria a mesma coisa com um número. */}
              <p className="hugi-saude-frase">{n.frase}</p>
              {curva.length >= 2 ? (
                <div className="hugi-saude-traco"><Curva pontos={curva} altura={96} /></div>
              ) : (
                <p className="hugi-saude-fraco">A curva começa depois do segundo dia medido.</p>
              )}
            </div>
          ),
        }
      : {
          id: "curva",
          area: "curva",
          titulo: "Ainda não dá para pontuar este número",
          rotuloCurto: "Falta medir",
          conteudo: <FaltaMedir s={s} />,
        },
    {
      id: "meta",
      area: "meta",
      titulo: "Meta e exposição",
      rotuloCurto: "Meta",
      conteudo: (
        <div className="hugi-saude-meta hugi-saude-miolo">
          <NotaDaMeta n={n} />
          <Exposicao n={n} />
        </div>
      ),
    },
    {
      id: "indicadores",
      area: "indicadores",
      titulo: "Indicadores",
      dica: "Peso riscado: o indicador não entrou no índice neste período (sem amostra, ou amostra abaixo do mínimo).",
      conteudo: (
        <table className="v2-tabela is-compacta hugi-saude-tabela">
          <thead>
            <tr>
              <th scope="col">Indicador</th>
              <th scope="col" className="is-num">Valor</th>
              <th scope="col" className="is-num">Amostra</th>
              <th scope="col" className="is-num">Peso</th>
            </tr>
          </thead>
          <tbody>
            {s.indicadores.map((i, k) => <LinhaDeIndicador key={`${i.nome}-${k}`} i={i} />)}
          </tbody>
        </table>
      ),
    },
    ...(sinais.length > 0
      ? [{
          id: "sinais",
          area: "sinais",
          titulo: "Sinais no período",
          rotuloCurto: "Sinais",
          conteudo: (
            <ul className="hugi-saude-sinais hugi-saude-miolo">
              {sinais.map(([nome, q]) => (
                <li key={nome}>{SINAL[nome] ?? nome}: {q.toLocaleString("pt-BR")}</li>
              ))}
            </ul>
          ),
        } satisfies CartaoDaGrade]
      : []),
  ];

  return (
    <PaginaInteira rotulo={`Saúde do número ${identificadorLegivel(n.rotulo)}`} classe="hugi-saude">
      {cabecalho(
        identificadorLegivel(n.rotulo),
        <SeloDoTom tom={tomDaFaixa(s.faixa)}>{nomeDaFaixa(s.faixa)}</SeloDoTom>,
        pedeSegurar ? <PausarDoNumero conexaoId={props.id} classe="v2-botao is-primario" /> : undefined,
      )}

      {s.punicoesAtivas.length > 0 ? (
        <section className="v2-cartao hugi-saude-punicoes" aria-label="Punições ativas">
          {s.punicoesAtivas.map((p, k) => <SeloDoTom key={`${p}-${k}`} tom="ruim">{nomeDaPunicao(p)}</SeloDoTom>)}
          {s.teto !== null ? (
            <span className="hugi-saude-fraco hugi-saude-miudo">o índice não passa de {s.teto} enquanto isto valer</span>
          ) : null}
        </section>
      ) : null}

      {/*
        A FAIXA: o índice nosso AO LADO da nota da Meta, e nunca somados. Só com índice — sem ele, o
        cartão "Ainda não dá para pontuar" diz quanto falta, e um "—" aqui seria a quinta maneira de
        dizer *não sei*.
      */}
      {comIndice ? (
        <PainelDeIndicadores
          faixa
          rotulo="Números do período"
          indicadores={[
            {
              id: "indice",
              rotulo: "Índice do Hugi",
              valor: String(s.indice),
              detalhe: `${s.componentes.usados} de ${s.componentes.total} indicadores`,
              tom: NO_KIT[tomDaFaixa(s.faixa)],
            },
            {
              id: "variacao",
              rotulo: "Variação",
              valor: n.variacao === null || n.variacao === undefined ? null : `${n.variacao > 0 ? "+" : ""}${n.variacao}`,
              detalhe: "no período",
              ...(n.variacao !== null && n.variacao !== undefined && n.variacao < 0 ? { tom: "perigo" as const } : {}),
            },
            {
              id: "meta",
              rotulo: "Nota da Meta",
              valor: n.modo === "nao_oficial" ? null : n.qualidadeDaMeta,
              detalhe: n.modo === "nao_oficial" ? "não existe neste canal" : n.qualidadeDaMeta ? "medida deles" : "ainda não devolvida",
            },
          ]}
        />
      ) : null}

      <GradeDeCartoes
        rotulo="Saúde deste número"
        cartoes={cartoes}
        colunas="minmax(0, 1fr) minmax(0, 1fr)"
        areas={sinais.length > 0 ? '"curva indicadores" "meta indicadores" "sinais indicadores"' : '"curva indicadores" "meta indicadores"'}
        linhas={sinais.length > 0 ? "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)"}
      />
    </PaginaInteira>
  );
}
